import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { BASE_CONFIG } from '@libs/core/base-config';
import { asResult } from '@libs/core/lib/state/async-states';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { distinctUntilChanged, map } from 'rxjs';

export type AuthSessionState = {
  isAuthenticated: boolean | null;
  _redirectionHandled: boolean;
  _isSigningIn: boolean;
  _isSigningOut: boolean;
  _afterSigninInCallbacks: Record<string, () => void>[];
};

const isNative = Capacitor.isNativePlatform();

const SIGN_IN_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const SIGN_OUT_TIMEOUT_MS = 60 * 1000; // 1 minute

export const AuthStateStore = signalStore(
  { providedIn: 'root' },
  withState<AuthSessionState>({
    isAuthenticated: null,
    _redirectionHandled: false,
    _isSigningIn: false,
    _isSigningOut: false,
    _afterSigninInCallbacks: [],
  }),
  withMethods(
    (
      store,
      authService = inject(OidcSecurityService),
      destroyRef = inject(DestroyRef),
      config = inject(BASE_CONFIG),
    ) => {
      function cleanupListeners(...handles: { remove: () => Promise<void> }[]) {
        return Promise.allSettled(handles.map((h) => h.remove()));
      }

      return {
        checkAuthState(): boolean {
          if (store.isAuthenticated?.() === true) return true;
          const isAuthenticated = authService.authenticated().isAuthenticated;
          patchState(store, { isAuthenticated });
          return isAuthenticated;
        },

        async signIn() {
          if (store._isSigningIn()) {
            console.warn('Sign-in already in progress');
            return;
          }
          patchState(store, { _isSigningIn: true });
          try {
            if (isNative) {
              await this._handleNativeSignIn(config.authRedirectUri);
            } else {
              await asResult(authService.authorizeWithPopUp(undefined, { width: 600, height: 700 }));
              patchState(store, { _isSigningIn: false });
            }
          } catch (error) {
            console.error('Sign-in error:', error);
            patchState(store, { _isSigningIn: false });
          }
        },

        registerAfterSignInCallback(key: string, callback: () => void): void {
          patchState(store, (state) => ({
            _afterSigninInCallbacks: [...state._afterSigninInCallbacks, { [key]: callback }],
          }));
        },
        unregisterAfterSignInCallback(key: string): void {
          patchState(store, (state) => ({
            _afterSigninInCallbacks: state._afterSigninInCallbacks.filter((cb) => !cb[key]),
          }));
        },

        async _handleNativeSignIn(redirectUri: string): Promise<void> {
          patchState(store, { _redirectionHandled: false });

          const timeout = { id: undefined as ReturnType<typeof setTimeout> | undefined };

          const urlOpenHandle = await App.addListener('appUrlOpen', async ({ url }) => {
            if (!url.startsWith(redirectUri)) return;

            patchState(store, { _redirectionHandled: true });
            clearTimeout(timeout.id);
            try {
              const res = await asResult(authService.checkAuth(url));
              patchState(store, { isAuthenticated: res.success ? res.data.isAuthenticated : false });
              await Browser.close();
            } catch (error) {
              console.error('Error during native sign-in callback:', error);
              patchState(store, { isAuthenticated: false });
            } finally {
              await cleanupListeners(urlOpenHandle, browserFinishedHandle);
              patchState(store, { _isSigningIn: false });
            }
          });

          // Handle the case where the user closes the browser during the sign-in.
          const browserFinishedHandle = await Browser.addListener('browserFinished', async () => {
            // On Android, the browserFinished event will be triggered on deep link redirection,
            // and may arrive before the appUrlOpen event. We need to wait for a short period
            // to ensure the appUrlOpen event is handled first.
            const BROWSER_FINISHED_GRACE_PERIOD_MS = 150;
            await new Promise((r) => setTimeout(r, BROWSER_FINISHED_GRACE_PERIOD_MS));

            if (store._redirectionHandled()) return;

            clearTimeout(timeout.id);
            await cleanupListeners(urlOpenHandle, browserFinishedHandle);
            patchState(store, { _isSigningIn: false });
          });

          // Timeout safety net: clean up listeners if sign-in takes too long
          timeout.id = setTimeout(async () => {
            if (!store._redirectionHandled()) {
              console.warn('Native sign-in timed out');
              await cleanupListeners(urlOpenHandle, browserFinishedHandle);
              patchState(store, { _isSigningIn: false });
            }
          }, SIGN_IN_TIMEOUT_MS);

          authService.authorize(undefined, {
            urlHandler: (url) => {
              Browser.open({ url, windowName: '_self', presentationStyle: 'popover' });
            },
          });
        },

        async signOut() {
          if (store._isSigningOut()) {
            console.warn('Sign-out already in progress');
            return;
          }
          patchState(store, { _isSigningOut: true });
          try {
            if (isNative) {
              await this._handleNativeLogout(config.authRedirectUri);
            } else {
              await asResult(authService.logoffAndRevokeTokens());
            }
            patchState(store, { isAuthenticated: false });
          } catch (error) {
            console.error('Sign-out error:', error);
          } finally {
            patchState(store, { _isSigningOut: false });
          }
        },

        async _handleNativeLogout(postLogoutRedirectUri: string): Promise<void> {
          const timeout = { id: undefined as ReturnType<typeof setTimeout> | undefined };

          const handle = await App.addListener('appUrlOpen', async ({ url }) => {
            if (!url.startsWith(postLogoutRedirectUri)) return;

            clearTimeout(timeout.id);
            try {
              await Promise.all([Browser.close(), handle.remove()]);
            } catch (error) {
              console.error('Error during native sign-out cleanup:', error);
            }
          });

          // Timeout safety net
          timeout.id = setTimeout(async () => {
            console.warn('Native sign-out timed out');
            await handle.remove().catch((e: unknown) => console.error('Listener removal failed:', e));
          }, SIGN_OUT_TIMEOUT_MS);

          await asResult(
            authService.logoffAndRevokeTokens(undefined, {
              urlHandler: (url) => {
                Browser.open({ url, windowName: '_self', presentationStyle: 'popover' });
              },
            }),
          );
        },

        watchAuthState(): void {
          if (isNative) {
            // Native: Restore session from stored tokens.
            // Deep-link callbacks are handled separately by _handleNativeSignIn.
            asResult(authService.checkAuth()).then((res) => {
              // Don't overwrite state already set by a concurrent sign-in flow
              if (store.isAuthenticated() !== null) return;
              patchState(store, { isAuthenticated: res.success ? res.data.isAuthenticated : false });
            });
          } else {
            // Web: Process OIDC redirect callback if returning from IDP,
            // otherwise validate existing session tokens.
            asResult(authService.checkAuth()).then((res) => {
              patchState(store, { isAuthenticated: res.success ? res.data.isAuthenticated : false });
            });
          }

          // Reactively track auth state changes (token refresh, session expiry, etc.)
          authService.isAuthenticated$
            .pipe(
              map(({ isAuthenticated }) => isAuthenticated),
              distinctUntilChanged(),
              takeUntilDestroyed(destroyRef),
            )
            .subscribe({
              next: (isAuthenticated) => {
                console.log('Auth state changed:', isAuthenticated);
                patchState(store, { isAuthenticated });
                if (isAuthenticated) {
                  for (const callbackObj of store._afterSigninInCallbacks()) {
                    const callback = Object.values(callbackObj)[0];
                    callback();
                  }
                }
              },
              error: (err) => console.error('Auth state subscription error:', err),
            });
        },
      };
    },
  ),

  withHooks((store) => ({
    onInit() {
      store.watchAuthState();
    },
  })),
);
